//============================================================================================================
// Authorisation and Authentication
// This file is used to manage the identity of the user.
//============================================================================================================
import { decodeJwt } from 'jose';
import { getApiUrl } from "../apiEndPoints";
//------------------------------------------------------------------------------------------------------------
//                                              REGISTER
// Auth and current Bearer token required to register a new user at the api endpoint.
//------------------------------------------------------------------------------------------------------------
export const registerUser = (email, password) => {
    return new Promise((resolve, reject) => {
        const registerUrl = getApiUrl() + "Identity/register";
        const registerData = {
            email: email,
            password: password,
        };
        fetch(registerUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(registerData),
        })
            .then((response) => response.json())
            .then((data) => {
            if (data && data.token && data.claims) {
                const userCredential = {
                    token: data.token,
                    userClaims: data.claims.map((claim) => ({
                        claimType: claim.type,
                        claimValue: claim.value,
                    })),
                    errorMessage: null,
                };
                console.log("Registered successfully:", userCredential);
                // We are not storing the token as we don't want to log out the current user.
                resolve(userCredential);
            }
            else {
                reject(new Error("Invalid register response"));
            }
        })
            .catch((error) => {
            console.error("Error:", error);
            reject(error);
        });
    });
};
window.registerUser = registerUser;
//------------------------------------------------------------------------------------------------------------
//                                              LOGIN
//------------------------------------------------------------------------------------------------------------
export const login = (username, password) => {
    return new Promise((resolve, reject) => {
        const loginUrl = getApiUrl() + "Identity/login";
        const loginData = {
            username: username,
            password: password,
        };
        fetch(loginUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(loginData),
        })
            .then((response) => {
            if (!response.ok) {
                if (response.status === 401) {
                    // Handle unauthorized error
                    const errorUserCredential = {
                        token: "",
                        userClaims: [],
                        errorMessage: "Unauthorized: Invalid username or password",
                    };
                    reject(errorUserCredential);
                    return;
                }
            }
            return response.json();
        })
            .then((data) => {
            if (data.token && data.claims) {
                const userCredential = {
                    token: data.token,
                    userClaims: data.claims.map((claim) => ({
                        type: claim.type,
                        value: claim.value,
                    })),
                    errorMessage: null,
                };
                resolve(userCredential);
            }
            else {
                reject(new Error("Invalid login response"));
            }
        })
            .catch((error) => {
            const errorUserCredential = {
                token: "",
                userClaims: [],
                errorMessage: "Unauthorized: Invalid username or password",
            };
            reject(errorUserCredential);
        });
    });
};
//------------------------------------------------------------------------------------------------------------
//                                              USER CLAIMS
//          Get the user claims from the server, If the token is valid it will return the user claims.
//------------------------------------------------------------------------------------------------------------
export const getUserClaims = () => {
    return new Promise((resolve, reject) => {
        const token = localStorage.getItem("token");
        const claimsUrl = getApiUrl() + "Identity/claims";
        fetch(claimsUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((response) => response.json())
            .then((data) => {
            if (data && data.claims && Array.isArray(data.claims)) {
                const claims = data.claims.map((claim) => ({
                    type: claim.type,
                    value: claim.value,
                }));
                console.log("Retrieved claims:", claims);
                resolve(claims);
            }
            else {
                reject(new Error("Invalid claims response"));
            }
        })
            .catch((error) => {
            console.error("Error:", error);
            reject(error);
        });
    });
};
//------------------------------------------------------------------------------------------------------------
//                                              USER CLAIMS
//          Get the user claims from the server, If the roles are in the user claims it will return true.
//          The API will not return data if the user is not in the roles anyway,
//             but this is to enforce client side too.
//          User only needs to be in at least one of the roles to return true.
//------------------------------------------------------------------------------------------------------------
export const getIsUserInAnyOfTheseRoles = (roles) => {
    return new Promise((resolve, reject) => {
        if (roles.length === 0) {
            resolve(false);
        }
        const token = localStorage.getItem("token");
        const claimsUrl = getApiUrl() + "Identity/claims";
        fetch(claimsUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((response) => {
            if (response.status === 401) {
                return response;
            }
            else {
                return response.json();
            }
        })
            .then((data) => {
            if (data && data.claims && Array.isArray(data.claims)) {
                const claims = data.claims.map((apiClaim) => ({
                    type: apiClaim.type,
                    value: apiClaim.value,
                }));
                // Check if the user is in any of the roles and resolve true if they are.
                for (let i = 0; i < roles.length; i++) {
                    if (claims.find((claim) => claim.type === "Role" && claim.value === roles[i])) {
                        resolve(true);
                        return;
                    }
                }
                resolve(false);
            }
            else {
                resolve(false);
                return;
            }
        })
            .catch((error) => {
            reject(error);
        });
    });
};
//------------------------------------------------------------------------------------------------------------
//                                              REFRESH TOKEN
//          Refresh the token, If the token is valid it will return the new token.
//------------------------------------------------------------------------------------------------------------
export const refreshToken = () => {
    return new Promise((resolve, reject) => {
        const token = localStorage.getItem("token");
        const refreshTokenUrl = getApiUrl() + "Identity/refresh-token";
        fetch(refreshTokenUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((response) => {
            if (!response.ok) {
                if (response.status === 401) {
                    // Handle unauthorized error
                    reject(new Error("Unauthorized: Invalid token"));
                    return;
                }
            }
            return response.json();
        })
            .then((data) => {
            if (data && data.token) {
                const newToken = data.token;
                console.log("Refreshed token:", newToken);
                resolve(newToken);
            }
            else {
                reject(new Error("Invalid refresh response"));
            }
        })
            .catch((error) => {
            console.error("Error:", error);
            reject(error);
        });
    });
};
//------------------------------------------------------------------------------------------------------------
//                                              LOGOUT
//------------------------------------------------------------------------------------------------------------
export const logout = () => {
    // invalidate the token at the api endpoint too.
    const logoutUrl = getApiUrl() + "Identity/logout";
    const token = localStorage.getItem("token");
    //Remove from local storage
    localStorage.removeItem("token");
    // post to the logout endpoint with token to logout and invalidate the token api side.
    fetch(logoutUrl, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
        },
    })
        .then((response) => {
        console.log("Logged out successfully");
    })
        .catch((error) => {
        console.error("Error:", error);
    });
};
export function getTokenExpiry(token) {
    try {
        if (!token) {
            return null;
        }
        const decodedToken = decodeJwt(token);
        if (decodedToken && decodedToken.exp) {
            // Convert the expiration time from seconds to milliseconds
            return decodedToken.exp * 1000;
        }
    }
    catch (error) {
        console.error('Error decoding token:', error);
    }
    return null;
}
export function isTokenExpired(token) {
    const expiryTime = getTokenExpiry(token);
    if (expiryTime) {
        return Date.now() > expiryTime;
    }
    return false;
}
